import { createClient, type Session } from '@supabase/supabase-js';
import { Platform } from 'react-native';
import { MMKV } from 'react-native-mmkv';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { SupabaseLogger } from '../utils/logger';
import { getSupabasePublicApiKey, getSupabaseUrl } from './supabaseEnv';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '../config/recoveryDiagnostic';
import {
  RECOVERY_DIAGNOSTICS_BUCKET,
  RECOVERY_DIAGNOSTICS_PUBLIC_KEY_ID,
} from '../config/recoveryDiagnostic';
import {
  isRecoverySupabaseRequestAllowed,
  type RecoveryNetworkScope,
} from './recovery/recoveryNetworkPolicy';

// Criar instancia do logger
const logger = SupabaseLogger.Main;
const sessionLogger = SupabaseLogger.Session;

const SUPABASE_URL = getSupabaseUrl();
const {
  key: SUPABASE_PUBLIC_API_KEY,
  source: SUPABASE_PUBLIC_API_KEY_SOURCE,
} = getSupabasePublicApiKey();

let allowRecoverySessionStorageWrite = false;

if (!SUPABASE_URL || !SUPABASE_PUBLIC_API_KEY) {
  const missing = [
    !SUPABASE_URL ? 'EXPO_PUBLIC_SUPABASE_URL' : null,
    !SUPABASE_PUBLIC_API_KEY
      ? 'EXPO_PUBLIC_SUPABASE_PUBLISHABLE_KEY ou EXPO_PUBLIC_SUPABASE_ANON_KEY'
      : null,
  ].filter(Boolean).join(', ');

  logger.error(`Configuracao publica do Supabase ausente: ${missing}`);
  throw new Error(`Configuracao publica do Supabase ausente: ${missing}`);
}

const SUPABASE_AUTH_STORAGE_KEY = `sb-${new URL(SUPABASE_URL).hostname.split('.')[0]}-auth-token`;

if (SUPABASE_PUBLIC_API_KEY_SOURCE === 'anon') {
  logger.warn(
    'Usando EXPO_PUBLIC_SUPABASE_ANON_KEY legacy como fallback temporario. Configure EXPO_PUBLIC_SUPABASE_PUBLISHABLE_KEY.'
  );
}
// Configurar storage customizado baseado na plataforma
let customStorage;

// Se a plataforma for web, utiliza o localStorage
if (Platform.OS === 'web') {
  logger.info('Inicializando storage para web (localStorage)');
  customStorage = {
    getItem: (key: string): Promise<string | null> => {
      try {
        const value = window.localStorage.getItem(key);
        if (!IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          SupabaseLogger.logStorageState(key, value, 'localStorage.getItem');
        }
        return Promise.resolve(value);
      } catch (error) {
        sessionLogger.error('Erro ao obter item do localStorage:', error);
        return Promise.resolve(null);
      }
    },
    setItem: (key: string, value: string): Promise<void> => {
      try {
        if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC && !allowRecoverySessionStorageWrite) {
          return Promise.resolve();
        }
        window.localStorage.setItem(key, value);
        if (!IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          SupabaseLogger.logStorageState(key, value, 'localStorage.setItem');
        }
        return Promise.resolve();
      } catch (error) {
        sessionLogger.error('Erro ao definir item no localStorage:', error);
        if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC && allowRecoverySessionStorageWrite) {
          return Promise.reject(error);
        }
        return Promise.resolve();
      }
    },
    removeItem: (key: string): Promise<void> => {
      try {
        if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          return Promise.resolve();
        }
        window.localStorage.removeItem(key);
        sessionLogger.debug(`removeItem: ${key} removido do localStorage`);
        return Promise.resolve();
      } catch (error) {
        sessionLogger.error('Erro ao remover item do localStorage:', error);
        return Promise.resolve();
      }
    },
  };
} else {
  // Em mobile, instancie o MMKV
  logger.info('Inicializando storage para mobile (MMKV)');
  const mmkv = new MMKV();
  customStorage = {
    getItem: (key: string): Promise<string | null> => {
      try {
        const value = mmkv.getString(key);
        if (!IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          SupabaseLogger.logStorageState(key, value, 'MMKV.getString');
        }
        return Promise.resolve(value ?? null);
      } catch (error) {
        sessionLogger.error('Erro ao obter item do MMKV:', error);
        return Promise.resolve(null);
      }
    },
    setItem: (key: string, value: string): Promise<void> => {
      try {
        if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC && !allowRecoverySessionStorageWrite) {
          return Promise.resolve();
        }
        mmkv.set(key, value);
        if (!IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          SupabaseLogger.logStorageState(key, value, 'MMKV.set');
        }
        return Promise.resolve();
      } catch (error) {
        sessionLogger.error('Erro ao definir item no MMKV:', error);
        if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC && allowRecoverySessionStorageWrite) {
          return Promise.reject(error);
        }
        return Promise.resolve();
      }
    },
    removeItem: (key: string): Promise<void> => {
      try {
        if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
          return Promise.resolve();
        }
        mmkv.delete(key);
        sessionLogger.debug(`removeItem: ${key} removido do MMKV`);
        return Promise.resolve();
      } catch (error) {
        sessionLogger.error('Erro ao remover item do MMKV:', error);
        return Promise.resolve();
      }
    },
  };
}

// Criar o cliente Supabase com o storage customizado
const nativeFetch = globalThis.fetch.bind(globalThis);
const RECOVERY_AUTH_TIMEOUT_MS = 20_000;
const RECOVERY_STORAGE_TIMEOUT_MS = 45_000;

const recoveryBlockedFetch: typeof globalThis.fetch = async () => {
  throw new Error('Rede Supabase bloqueada pelo modo de recuperacao.');
};

const recoveryFetchWithTimeout = async (
  requestUrl: string,
  init: RequestInit,
  timeoutMs: number
): Promise<Response> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await nativeFetch(requestUrl, {
      ...init,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }
};

export const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLIC_API_KEY, {
  ...(IS_CHECKLIST_RECOVERY_DIAGNOSTIC
    ? { global: { fetch: recoveryBlockedFetch } }
    : {}),
  auth: {
    storage: customStorage,
    storageKey: SUPABASE_AUTH_STORAGE_KEY,
    autoRefreshToken: !IS_CHECKLIST_RECOVERY_DIAGNOSTIC,
    persistSession: !IS_CHECKLIST_RECOVERY_DIAGNOSTIC,
    detectSessionInUrl: Platform.OS === 'web' && !IS_CHECKLIST_RECOVERY_DIAGNOSTIC,
  },
});

/** Cria um cliente de requisições sem estado de auth mutável, preso ao JWT capturado. */
export const createSessionBoundSupabaseClient = (accessToken: string) => createClient(
  SUPABASE_URL!,
  SUPABASE_PUBLIC_API_KEY,
  {
    accessToken: async () => accessToken,
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
  }
);

// Função auxiliar para recuperar a sessão local
export const getSession = async () => {
  sessionLogger.debug('Recuperando sessão...');
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    return readRecoveryStoredSession();
  }
  const { data, error } = await supabase.auth.getSession();
  
  if (error) {
    sessionLogger.error('Erro ao recuperar sessão:', error);
    return null;
  }
  
  if (data?.session) {
    SupabaseLogger.logSessionState(data.session, 'getSession()');
    return data.session;
  } else {
    sessionLogger.debug('Nenhuma sessão encontrada');
    return null;
  }
};

const isStoredSession = (value: unknown): value is Session => {
  if (!value || typeof value !== 'object') return false;
  const session = value as Partial<Session>;
  return Boolean(
    session.access_token &&
    session.refresh_token &&
    session.user?.id
  );
};

const readRecoveryStoredSession = async (): Promise<Session | null> => {
  try {
    const raw = await customStorage.getItem(SUPABASE_AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isStoredSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const persistRecoverySession = async (session: Session): Promise<void> => {
  const serialized = JSON.stringify(session);
  let persisted: Session | null = null;

  allowRecoverySessionStorageWrite = true;
  try {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      await customStorage.setItem(SUPABASE_AUTH_STORAGE_KEY, serialized);
      persisted = await readRecoveryStoredSession();
      if (
        persisted?.access_token === session.access_token &&
        persisted.refresh_token === session.refresh_token
      ) {
        return;
      }
    }
  } finally {
    allowRecoverySessionStorageWrite = false;
  }

  throw new Error('Nao foi possivel preservar a sessao atualizada no aparelho.');
};

const assertRecoveryRequest = (
  scope: Exclude<RecoveryNetworkScope, 'blocked'>,
  requestUrl: string,
  method: string,
  expectedObjectPath?: string,
  upsertHeader?: string
): void => {
  if (!isRecoverySupabaseRequestAllowed({
    scope,
    supabaseUrl: SUPABASE_URL,
    requestUrl,
    method,
    expectedObjectPath,
    upsertHeader,
  })) {
    throw new Error('Requisicao de recuperacao recusada pela politica local.');
  }
};

const readResponseJson = async (response: Response): Promise<Record<string, unknown> | null> => {
  try {
    const value = await response.json();
    return value && typeof value === 'object' ? value as Record<string, unknown> : null;
  } catch {
    return null;
  }
};

const refreshRecoverySession = async (session: Session): Promise<Session> => {
  const requestUrl = `${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`;
  assertRecoveryRequest('auth_refresh', requestUrl, 'POST');

  const requestRefresh = () => recoveryFetchWithTimeout(requestUrl, {
    method: 'POST',
    redirect: 'error',
    headers: {
      apikey: SUPABASE_PUBLIC_API_KEY,
      authorization: `Bearer ${SUPABASE_PUBLIC_API_KEY}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: session.refresh_token }),
  }, RECOVERY_AUTH_TIMEOUT_MS);
  let response: Response;
  try {
    response = await requestRefresh();
  } catch {
    // O retry imediato aproveita a janela de reutilizacao do refresh token
    // quando o servidor rotacionou o token, mas a primeira resposta se perdeu.
    response = await requestRefresh();
  }
  const payload = await readResponseJson(response);
  if (!response.ok || !payload) {
    throw new Error('A sessao nao pode ser atualizada. Abra primeiro o app normal e tente novamente.');
  }

  const refreshed = {
    ...session,
    ...payload,
    user: payload.user ?? session.user,
    expires_at: Number(payload.expires_at) ||
      Math.floor(Date.now() / 1000) + Number(payload.expires_in || 0),
  } as Session;
  if (!isStoredSession(refreshed) || refreshed.user.id !== session.user.id) {
    throw new Error('A resposta de renovacao da sessao foi recusada.');
  }

  await persistRecoverySession(refreshed);
  return refreshed;
};

const getRecoveryUploadSession = async (forceRefresh = false): Promise<Session> => {
  const session = await readRecoveryStoredSession();
  if (!session) {
    throw new Error('Sessao local ausente. Nao desinstale nem limpe os dados do aplicativo.');
  }

  const expiresAt = typeof session.expires_at === 'number' ? session.expires_at : 0;
  const expired = expiresAt <= Math.floor(Date.now() / 1000);
  return forceRefresh || expired ? refreshRecoverySession(session) : session;
};

const assertRecoveryObjectPath = (userId: string, objectPath: string): void => {
  const escapedUserId = userId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const expected = new RegExp(
    `^${escapedUserId}/[0-9]{8}T[0-9]{6}Z-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\\.agrec$`,
    'i'
  );
  if (!expected.test(objectPath)) {
    throw new Error('Caminho do diagnostico invalido.');
  }
};

export interface RecoveryStorageUploadResult {
  path: string;
  duplicate: boolean;
  sha512: string;
}

export const uploadRecoveryDiagnosticObject = async (
  objectPath: string,
  body: ArrayBuffer
): Promise<RecoveryStorageUploadResult> => {
  if (!IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    throw new Error('Upload de recuperacao indisponivel nesta versao.');
  }

  let session = await getRecoveryUploadSession();
  assertRecoveryObjectPath(session.user.id, objectPath);
  if (body.byteLength <= 0 || body.byteLength > 25 * 1024 * 1024) {
    throw new Error('Tamanho do diagnostico fora do limite permitido.');
  }

  const fileId = objectPath.match(/Z-([0-9a-f-]{36})\.agrec$/i)?.[1];
  if (!fileId) {
    throw new Error('Identificador do diagnostico invalido.');
  }
  const identifier = fileId.slice(0, 8).toUpperCase();
  const sha512 = naclUtil.encodeBase64(nacl.hash(new Uint8Array(body)));
  const metadata = naclUtil.encodeBase64(naclUtil.decodeUTF8(JSON.stringify({
    sha512,
    bytes: body.byteLength,
    identifier,
    recipientKeyId: RECOVERY_DIAGNOSTICS_PUBLIC_KEY_ID,
  })));
  const uploadUrl = `${SUPABASE_URL}/storage/v1/object/${RECOVERY_DIAGNOSTICS_BUCKET}/${objectPath}`;
  const infoUrl = `${SUPABASE_URL}/storage/v1/object/info/${RECOVERY_DIAGNOSTICS_BUCKET}/${objectPath}`;

  const requestUpload = (accessToken: string) => {
    assertRecoveryRequest('storage_upload', uploadUrl, 'POST', objectPath, 'false');
    return recoveryFetchWithTimeout(uploadUrl, {
      method: 'POST',
      redirect: 'error',
      headers: {
        apikey: SUPABASE_PUBLIC_API_KEY,
        authorization: `Bearer ${accessToken}`,
        'content-type': 'application/octet-stream',
        'cache-control': 'max-age=0',
        'x-upsert': 'false',
        'x-metadata': metadata,
      },
      body,
    }, RECOVERY_STORAGE_TIMEOUT_MS);
  };

  const confirmExisting = async (accessToken: string): Promise<boolean> => {
    assertRecoveryRequest('storage_info', infoUrl, 'GET', objectPath);
    const infoResponse = await recoveryFetchWithTimeout(infoUrl, {
      method: 'GET',
      redirect: 'error',
      headers: {
        apikey: SUPABASE_PUBLIC_API_KEY,
        authorization: `Bearer ${accessToken}`,
      },
    }, RECOVERY_AUTH_TIMEOUT_MS);
    const info = await readResponseJson(infoResponse);
    if (!infoResponse.ok || !info) return false;
    const remoteMetadata = info.metadata && typeof info.metadata === 'object'
      ? info.metadata as Record<string, unknown>
      : null;
    return (
      Number(info.size) === body.byteLength &&
      remoteMetadata?.sha512 === sha512 &&
      Number(remoteMetadata?.bytes) === body.byteLength &&
      remoteMetadata?.identifier === identifier &&
      remoteMetadata?.recipientKeyId === RECOVERY_DIAGNOSTICS_PUBLIC_KEY_ID
    );
  };

  let response = await requestUpload(session.access_token);
  if (response.status === 401) {
    session = await getRecoveryUploadSession(true);
    assertRecoveryObjectPath(session.user.id, objectPath);
    response = await requestUpload(session.access_token);
  }

  if (!response.ok) {
    if ([400, 409].includes(response.status) && await confirmExisting(session.access_token)) {
      return { path: objectPath, duplicate: true, sha512 };
    }
    throw new Error('Nao foi possivel enviar o diagnostico. Tente novamente com internet.');
  }

  const uploaded = await readResponseJson(response);
  const expectedKey = `${RECOVERY_DIAGNOSTICS_BUCKET}/${objectPath}`;
  if (!uploaded || uploaded.Key !== expectedKey) {
    throw new Error('O Storage nao confirmou o caminho esperado do diagnostico.');
  }
  return { path: objectPath, duplicate: false, sha512 };
};
